import { ChefHat } from 'lucide-react';
import { translator } from './i18n';
import { normalizedRecipeMeasureUnit, recipeMeasureLabel } from './recipeMeasures';
import { unitLabel } from './units';
import { D } from './exactDecimal';
import type { Locale, RestaurantRecipe, SupplyItem } from './types';
import { aggregateRecipeFoodTags, FOOD_TAG_CAUTION } from './foodTags';
import { FoodTagBadges } from './FoodTagIcons';
import './supplyFoodLabels.css';

export function recipeUnitLabel(value: string, locale: Locale): string {
  const known = normalizedRecipeMeasureUnit(value);
  if (known) return recipeMeasureLabel(known, locale);
  if (['batch', 'batches', 'lote', 'lotes'].includes(value.trim().toLowerCase())) return locale === 'es' ? 'Lote' : 'Batch';
  return unitLabel(value, locale);
}

/** Prefer live ingredient labels; a server summary covers ingredients outside the local scope. */
export function recipeFoodTagSummary(recipe: RestaurantRecipe, items?: readonly SupplyItem[]) {
  return items && recipe.ingredients.every((line) => items.some((item) => item.id === line.itemId))
    ? aggregateRecipeFoodTags(recipe, items)
    : recipe.foodTagSummary ?? aggregateRecipeFoodTags(recipe, items || []);
}

export function RecipeFoodTagBadges({ recipe, items, locale, compact = false }: { recipe: RestaurantRecipe; items?: readonly SupplyItem[]; locale: Locale; compact?: boolean }) {
  const summary = recipeFoodTagSummary(recipe, items);
  return <FoodTagBadges tags={summary.tags} needsReview={summary.needsReview} locale={locale} compact={compact} />;
}

/** Shared recipe presentation. It never records production or changes a recipe. */
export function RecipePhoto({ recipe, locale }: { recipe: RestaurantRecipe; locale: Locale }) {
  const t = translator(locale);
  return <div className="employee-recipe-hero">{recipe.image?.url ? <img src={recipe.image.url} alt={recipe.name[locale]} /> : <div><ChefHat size={36} /><span>{t('noRecipePhoto')}</span></div>}</div>;
}

export function RecipeDetails({ recipe, items, locale, multiplier = 1, instructionsFallback, production = false, showYield = false }: { recipe: RestaurantRecipe; items?: readonly SupplyItem[]; locale: Locale; multiplier?: number | string; instructionsFallback?: string; production?: boolean; showYield?: boolean }) {
  const t = translator(locale);
  const quantity = (value: number) => value.toLocaleString(locale === 'es' ? 'es-US' : 'en-US', { maximumFractionDigits: 12 });
  return <>
    <section className="recipe-food-labels"><strong>{locale === 'es' ? 'Alérgenos y características' : 'Allergens and food characteristics'}</strong><RecipeFoodTagBadges recipe={recipe} items={items} locale={locale} /><p>{FOOD_TAG_CAUTION[locale]}</p></section>
    {showYield && <div className="menu-recipe-yield"><span>{t('recipeYield')}: <strong>{quantity(recipe.yieldQuantity)} {recipeUnitLabel(recipe.yieldUnit, locale)}</strong></span>{recipe.portion && <span>{t('portionSize')}: <strong>{quantity(recipe.portion.size)} {recipeMeasureLabel(recipe.portion.unit, locale)}</strong></span>}</div>}
    <div className="employee-recipe-details">
      <section className="employee-recipe-section"><div className="recipe-section-heading"><strong>{t('ingredients')}</strong>{production && <span>{t('ingredientsForProduction')}</span>}</div><div className="employee-ingredient-list">{recipe.ingredients.map((line) => <div key={line.itemId}><span>{line.itemName[locale]}</span><b>{quantity(D(line.quantityExact ?? line.quantity).mul(multiplier).toNumber())} {recipeUnitLabel(line.unit, locale)}</b></div>)}</div></section>
      <section className="employee-recipe-section preparation-section"><div className="recipe-section-heading"><strong>{t('preparation')}</strong><span>{t('followPreparation')}</span></div><p>{recipe.instructions || instructionsFallback || t('noInstructions')}</p></section>
    </div>
  </>;
}
